import React, { FunctionComponent, useState } from 'react';

import styles from './InstagramPostCard.module.scss';
import PostCardTopBar from '../PostCardTopBar';
import ConversationPopup from '../ConversationPopup';
import UserInformation from '../UserInformation';
import { checkTranslated } from 'app/utils/helpers';
import {
  useHandleNavigateInfluencerOrEntity,
  useOpenModalToAddUserAsInfluencer,
} from 'app/utils/check-influencer-user';
import { storeInfluencerInstagram } from 'influencers/Requests';
import { _t } from 'app/utils/i18n';
import PostCardTextContent from '../PostCardTextContent';
import { path } from 'app/utils/path';
import DateLocation from '../DateLocation';
import InstagramPostCardStatus from '../InstagramPostCardStatus';
import PostCardMedia from '../PostCardMedia';
import { useModal } from 'app/context/Modal';
import TweetCardContent from '../TweetCardContent';
import { toast } from 'react-toastify';
import { useMutation } from 'react-query';
import { getCommentsDataList, getRegrabReplies } from 'posts/Requests';
import InstagramCardReportUi from '../InstagramCardReportUi';

interface Props {
  data: any;
  compId?: string;
  showConversationPopupFlag?: boolean;
  isReport?: boolean;
  isContentClickable?: boolean;
  post_type?: 'negative' | 'positive' | 'neutral' | 'all';
  showRegrab?: boolean;
  isPost?: boolean;
}

const InstagramPostCard: FunctionComponent<Props> = ({
  data,
  compId,
  showConversationPopupFlag,
  isReport,
  isContentClickable,
  post_type,
  showRegrab,
  isPost = true,
}) => {
  const [isTranslated, setIsTranslated] = useState(false);
  const [showConversationPopup, setShowConversationPopup] = useState(false);
  const pathname = location?.pathname?.split('/')?.[1];
  const [openModalToAddUserAsInfluencer] = useOpenModalToAddUserAsInfluencer(
    storeInfluencerInstagram,
  );

  const [handleNavigateInfluencerOrEntity] =
    useHandleNavigateInfluencerOrEntity();
  const modal = useModal();
  const handelOpenImageVideo = (index = 0) => {
    modal.show({
      imageUrls: data?.attachments,
      imageIndex: index,
    });
  };
  const RepliesConversationsMutation = useMutation(getRegrabReplies, {
    onSuccess: () => {
      modal.show({
        onOkText: _t('ok'),
        message: `${_t('basic:working_on_grabbing')} ${_t('posts:reply_conversation')} ${_t(
          'basic:you_will_be_notified',
        )}`,
        onOk: () => {
          modal.hide();
        },
        hideCancel: true,
      });
    },
    onError: (err: any) => {
      toast.error(<span>{err?.response?.data?.message}</span>);
    },
  });
  const regrabRepliesMenuOption = [
    {
      title: _t('posts:regrab_reply_conversation'),
      mutateFun: RepliesConversationsMutation,
      lastGrabbed: data?.comment_last_grabbed_on,
    },
  ];
  return (
    <div
      id={`${compId}-InstagramPostCard-${data.id}`}
      className={`${styles.InstagramPostCard} post_card_url`}
      data-type={data?.post_url}
    >
      {showConversationPopup && (
        <ConversationPopup
          commentsConversation={{ postData: data }}
          isOpen={showConversationPopup}
          handleOpen={() => setShowConversationPopup(true)}
          handleClose={() => setShowConversationPopup(false)}
          getCommentsDataList={getCommentsDataList}
          getRegrabReplies={getRegrabReplies}
          openModalToAddInfluencer={(username: string, url: string) =>
            openModalToAddUserAsInfluencer(
              username,
              url,
              _t('hashtags:open_in_instagram'),
            )
          }
        />
      )}
      <div className={styles.InstagramPostCard_Container}>
        {!isReport && (
          <PostCardTopBar
            platformUrl={data?.post_url}
            platform={data?.platform}
            postId={data?.id}
            compId={`${compId}-InstagramPostCard-${data.id}`}
            translationToggle={() => setIsTranslated((prev) => !prev)}
            postLanguages={data?.languages}
            showSponsored={data?.is_sponsored}
            showReplyIcon={data?.is_reply}
            content={data?.caption || data?.text}
            showConversationPopupFlag={showConversationPopupFlag}
            showConversationPopup={() => setShowConversationPopup(true)}
            extraOptions={!data?.is_reply ? regrabRepliesMenuOption : []}
            showRegrab={showRegrab}
            showPinned={data?.is_pinned}
          />
        )}
        <div className={styles.InstagramPostCard_Container_content}>
          <UserInformation
            profileImage={data?.profile_image_url}
            name={
              checkTranslated(
                data?.influencer?.name?.original,
                data?.influencer?.name?.arabic,
              ) || data?.username
            }
            isSmallerSize={
              pathname === 'influencers' || pathname === 'entities'
            }
            username={data?.username}
            isVerified={data?.is_verified}
            handleInfluencerOrEntity={() =>
              handleNavigateInfluencerOrEntity(data?.influencer)
            }
            postType={post_type}
            audio_owner_name={data?.audio_owner_name}
            audio_name={data?.audio_name}
            isClickable={!isReport}
            isUserAdded={data?.influencer?.influencer_id !== 0}
            openModalToAddInfluencer={() =>
              openModalToAddUserAsInfluencer(
                data?.username,
                data?.url,
                _t('hashtags:open_in_instagram'),
              )
            }
          />
          {(!isReport || (isReport && !isPost)) && (
            <PostCardTextContent
              content={data?.caption || data?.text}
              storeInfluencerName={storeInfluencerInstagram}
              detailsUrl={`${path.posts.INSTAGRAM_POSTS_DETAILS_URI}/${data?.id}`}
              isContentClickable={isContentClickable}
              isReport={isReport}
              isTranslated={isTranslated}
              mentions={data?.post_mentions}
              openText={_t('hashtags:open_in_instagram')}
            />
          )}
          {isReport && <div style={{ margin: '1rem' }}></div>}
          {data?.parent_comment && data?.parent_comment?.id !== 0 && (
            <TweetCardContent
              parentTweetData={data?.parent_comment}
              openModalToAddInfluencer={(url,username) =>
                openModalToAddUserAsInfluencer(
                  username,
                  url,
                  _t('hashtags:open_in_instagram'),
                )
              }
              handleInfluencer={(influencer) =>
                handleNavigateInfluencerOrEntity(influencer)
              }
              isQuote
              isInstagram
            />
          )}
          {data?.attachments?.length !== 0 && (
            <PostCardMedia {...{ data, handelOpenImageVideo }} />
          )}
          {isReport && isPost && (
            <InstagramCardReportUi
              comments_count={data?.comments_count}
              username={data?.username}
              likes_count={data?.likes_count}
              content={data?.caption?.original}
            />
          )}
          <div style={{ margin: '10px 0px' }}>
            <DateLocation
              is_caption_edited={data?.is_caption_edited}
              created_at={data?.published_at}
              location={data?.location}
            />
          </div>

          {(!isReport || (isReport && !isPost)) && (
            <InstagramPostCardStatus
              likes_count={data?.likes_count}
              post_type={data?.post_type}
              views_count={data?.views_count}
              comments_count={data?.comments_count}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default InstagramPostCard;
